import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MapContainer as LeafletMapContainer } from 'react-leaflet';
import styled from 'styled-components';

import UriHelper from '../../lib/uri-helper';
import Analytics from '../../lib/user-analytics';
import { SideDrawerMode } from '../../store/SideDrawer/model';
import Download from '../Drawer/Satellites/Sentinel/download';
import Share from '../Drawer/Share/Maps';
import Drawer from '../Drawer/drawer-control';
import { LoginModalMode } from '../Registration/login-enum';
import LoginRegisterDialog from '../Registration/login-register-dialog';
import SearchBar from '../Search/search-bar';
import UploadDialog from '../Upload/upload-dialog';
import ZoomControl from './Annotations/Zoom/zoom-control';
import Annotations from './Annotations/annotations';
import SatelliteProviders from './SatelliteProviders/satellite-providers';
import SatelliteLayers from './SatelliteTracker/satellite-layers';
import SearchResultMarker from './SearchResultMarker/search-result-marker';
import SentinelLayers from './Sentinel/sentinel-layers';
import TileLayers from './TileLayers/tilelayers';
import DroneUploadManualMarker from './UploadPreview/drone-upload-manual-marker';
import DroneUploadPreview from './UploadPreview/drone-upload-preview';
import Basemaps from './basemaps';
import MapScaleControl from './map-scale-control';
import MapViewDispatcher from './map-view-dispatcher';
import PinnedTileLayers from './pinned-tilelayers';
import TileLayerLoadingIndicator from './tilelayer-loading-indicator';
import { actionSetSideDrawerModeAction } from '../../store/SideDrawer/actions';
import { selectShowTutorial } from '../../store/App/selectors';
import SoarHelper from '../../lib/soar-helper';
import { actionDismissTutorial } from '../../store/App/actions';
import Tutorial from '../Tutorials/tutorial-starter';

interface MapContainerProps {
    loginMode?: LoginModalMode;
    drawerMode?: SideDrawerMode;
}

// Values that give the map an aesthetically pleasing start position
const WORLD_ZOOM = 2.5;
const WORLD_CENTER: [number, number] = [34.5, 0];
const MAX_ZOOM_LEVEL = 28;

const MapContainer = (props: MapContainerProps) => {
    const dispatch = useDispatch();
    const shouldShowTutorial = useSelector(selectShowTutorial);
    const [loginMode, setLoginMode] = useState<LoginModalMode | undefined>(props.loginMode);
    const [editMode, setEditMode] = useState(false);

    const onLoginDialogClosed = () => {
        setLoginMode(undefined);
        UriHelper.navigateToDrawer(SideDrawerMode.MAPS);
        Analytics.Event('Main View', 'Closed login dialog');
    };

    useEffect(() => {
        if (props.drawerMode) {
            dispatch(actionSetSideDrawerModeAction(props.drawerMode));
        }
    }, [props.drawerMode, dispatch]);

    return (
        <React.Fragment>
            {!editMode && <Drawer />}
            <Download />
            <Share />
            {!editMode && <SearchBar />}

            {shouldShowTutorial ? (
                <Tutorial
                    onSkip={() => {
                        dispatch(actionDismissTutorial());
                        if (SoarHelper.isSoar()) {
                            dispatch(
                                actionSetSideDrawerModeAction(props.drawerMode ? props.drawerMode : SideDrawerMode.MAPS)
                            );
                        } else {
                            dispatch(
                                actionSetSideDrawerModeAction(
                                    props.drawerMode ? props.drawerMode : SideDrawerMode.SUBDOMAIN_MAPS
                                )
                            );
                        }
                    }}
                />
            ) : null}

            <Container>
                <LeafletMapContainer
                    zoomSnap={0.01}
                    maxBoundsViscosity={1.0}
                    maxZoom={MAX_ZOOM_LEVEL}
                    zoom={WORLD_ZOOM}
                    center={WORLD_CENTER}
                    minZoom={WORLD_ZOOM}
                    className="leaflet-map"
                    id="leaflet-map"
                    zoomControl={false}
                >
                    <React.Fragment>
                        <MapViewDispatcher />

                        <ZoomControl />

                        {!editMode && <MapScaleControl />}
                        {!editMode && <PinnedTileLayers />}
                        <Basemaps hidden={editMode} />
                        {!editMode && <SatelliteLayers />}
                        <SatelliteProviders />

                        <TileLayerLoadingIndicator />
                        <TileLayers editMode={editMode} />
                        <SentinelLayers />

                        <SearchResultMarker />
                        <DroneUploadPreview />
                        <DroneUploadManualMarker />

                        <Annotations editMode={editMode} setEditMode={(isEditMode) => setEditMode(isEditMode)} />
                    </React.Fragment>
                </LeafletMapContainer>
            </Container>

            {loginMode && <LoginRegisterDialog isOpen={true} initialMode={loginMode} onClose={onLoginDialogClosed} />}
            <UploadDialog />
        </React.Fragment>
    );
};

export default MapContainer;

const Container = styled.div`
    position: absolute;
    top: 0px !important;
    bottom: 0px !important;
    width: 100vw;
    height: 100vh;
    overflow: hidden !important;
    overflow-y: hidden !important;
    overflow-x: hidden !important;
    padding: 0;
    margin: 0;

    @media (max-width: 500px) {
        position: absolute;
        top: 80px;
        bottom: 0px;
        width: 100%;
    }

    .leaflet-control-layers.leaflet-control a.leaflet-control-layers-toggle {
        vertical-align: bottom;
        display: table-cell;
    }

    .leaflet-right .leaflet-control {
        margin: 0;
        padding: 0;
    }

    .leaflet-bottom.leaflet-right
        .leaflet-control-layers.leaflet-control:first-of-type
        a.leaflet-control-layers-toggle {
        position: fixed;
        background-image: url('../../assets/map-categories/button_satellite.png') !important;
        bottom: 16px;
        right: 16px;
        z-index: 4000;
        background-color: rgba(0, 0, 0, 0.8) !important;
        border-radius: 6px !important;
        padding: 0px !important;
        width: 40px !important;
        height: 40px !important;
        background-position: 50% 50%;
        background-repeat: no-repeat;
    }

    .leaflet-bottom.leaflet-right
        .leaflet-control-layers.leaflet-control:nth-of-type(2)
        a.leaflet-control-layers-toggle {
        position: fixed;
        bottom: 16px;
        right: 72px;
        background-image: url('data:image/svg+xml,%3Csvg%20width%3D%2222%22%20height%3D%2222%22%20viewBox%3D%220%200%2022%2022%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M1%207.93861L11%201L21%207.93861L11%2014.4691L1%207.93861Z%22%20stroke%3D%22white%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M1%2011.2045L0.718466%2010.7913C0.580354%2010.8854%200.498388%2011.0423%200.500024%2011.2094C0.50166%2011.3765%200.586682%2011.5318%200.72661%2011.6232L1%2011.2045ZM11%2017.735L10.7266%2018.1536C10.8927%2018.2621%2011.1073%2018.2621%2011.2734%2018.1536L11%2017.735ZM21%2011.2045L21.2734%2011.6232C21.4127%2011.5322%2021.4976%2011.3779%2021.5%2011.2115C21.5023%2011.0452%2021.4217%2010.8886%2021.285%2010.7937L21%2011.2045ZM0.72661%2011.6232L10.7266%2018.1536L11.2734%2017.3163L1.27339%2010.7859L0.72661%2011.6232ZM20.7266%2010.7859L10.7266%2017.3163L11.2734%2018.1536L21.2734%2011.6232L20.7266%2010.7859ZM18.2598%209.91177L20.715%2011.6153L21.285%2010.7937L18.8299%209.09018L18.2598%209.91177ZM3.21847%209.08797L0.718466%2010.7913L1.28153%2011.6177L3.78153%209.91438L3.21847%209.08797Z%22%20fill%3D%22white%22%2F%3E%0A%3Cpath%20d%3D%22M1%2014.4695L0.714964%2014.0587C0.578275%2014.1536%200.497713%2014.3102%200.500049%2014.4765C0.502385%2014.6429%200.587312%2014.7972%200.72661%2014.8882L1%2014.4695ZM11%2021L10.7266%2021.4186C10.8927%2021.5271%2011.1073%2021.5271%2011.2734%2021.4186L11%2021ZM21%2014.4695L21.2734%2014.8882C21.4127%2014.7972%2021.4976%2014.6429%2021.5%2014.4765C21.5023%2014.3102%2021.4217%2014.1536%2021.285%2014.0587L21%2014.4695ZM0.72661%2014.8882L10.7266%2021.4186L11.2734%2020.5813L1.27339%2014.0509L0.72661%2014.8882ZM20.7266%2014.0509L10.7266%2020.5813L11.2734%2021.4186L21.2734%2014.8882L20.7266%2014.0509ZM18.362%2013.2477L20.715%2014.8803L21.285%2014.0587L18.9321%2012.4261L18.362%2013.2477ZM3.06791%2012.4261L0.714964%2014.0587L1.28504%2014.8803L3.63798%2013.2477L3.06791%2012.4261Z%22%20fill%3D%22white%22%2F%3E%0A%3C%2Fsvg%3E%0A') !important;
        z-index: 4000;
        background-color: rgba(0, 0, 0, 0.8) !important;
        border-radius: 6px !important;
        padding: 0px !important;
        width: 40px !important;
        height: 40px !important;
        background-position: 50% 50%;
        background-repeat: no-repeat;
    }

    .leaflet-bottom.leaflet-right
        .leaflet-control-layers.leaflet-control:nth-of-type(3)
        a.leaflet-control-layers-toggle {
        position: fixed;
        bottom: 16px;
        right: 128px;
        z-index: 4000;
        background-color: rgba(0, 0, 0, 0.8) !important;
        border-radius: 6px !important;
        padding: 0px !important;
        width: 40px !important;
        height: 40px !important;
        background-position: 50% 50%;
        background-repeat: no-repeat;
    }

    .leaflet-control-layers.leaflet-control.leaflet-control-layers-expanded:first-of-type {
        margin-bottom: 43px;
        margin-right: 0px;
        min-width: 126px;
    }

    .leaflet-control-layers.leaflet-control.leaflet-control-layers-expanded:nth-of-type(2) {
        margin-bottom: 43px;
        margin-right: 56px;
        min-width: 126px;
    }

    .leaflet-control-layers.leaflet-control.leaflet-control-layers-expanded:nth-of-type(3) {
        margin-bottom: 43px;
        margin-right: 112px;
    }
`;
